import { gsap } from 'gsap';
import type { Container } from 'pixi.js';
import { CharacterSprite } from '../CharacterSprite';
import type { AnimationAdapter, PlayOptions } from './AnimationAdapter';

// Global render constants - not per-character. Per-animation timings (speed,
// impact frames, hold durations) live on the character bundle and are pulled
// off CharacterSprite.timings when needed.
const HIT_COLOR = 0xff5555;
const HIT_FLASH_MS = 300;
// Calibrated for SPRITE_GAP=450 in BattleScene so silhouettes meet at peak.
const ATTACK_LUNGE_DISTANCE = 350;
// Counter fires while the original attacker is still at THEIR attack peak -
// already in melee range. So counter is a short jab, not a full lunge.
const COUNTER_JAB_DISTANCE = 30;
// Floor on leg-cycle speed during the decelerating lunge so the legs don't
// fully freeze in the last frames (where punch anim takes over anyway).
const RUN_MIN_SPEED_FACTOR = 0.3;

export class PlaceholderAdapter implements AnimationAdapter {
  load(_id: string): Promise<void> {
    return Promise.resolve();
  }

  stop(target: Container): void {
    gsap.killTweensOf(target);
    if (target instanceof CharacterSprite) {
      gsap.killTweensOf(target.body);
      gsap.killTweensOf(target.body.scale);
    }
  }

  play(id: string, target: Container, opts?: PlayOptions): Promise<void> {
    if (!(target instanceof CharacterSprite)) {
      return waitMs(150);
    }
    switch (id) {
      // Primary attack is split into THREE phases so the renderer can:
      //   - trigger the defender's reactive stance between run and swing,
      //   - fire `hit` at the swing's impact peak (during, not after).
      case 'attack_run':
        return this.attackRun(target);
      case 'attack_swing':
        return this.attackSwing(target, opts);
      case 'attack_return':
        return this.attackReturn(target);
      // Counter uses a short jab instead of a full lunge - the original attacker
      // is already in range at their own attack peak, so a long lunge would
      // overshoot through the attacker's body to their empty home position.
      case 'counter':
        return this.counterJab(target, opts);
      case 'block':
        return this.block(target, opts);
      case 'block_exit':
        return this.blockExit(target);
      case 'dodge':
        return this.dodge(target);
      case 'hit':
        return this.hit(target, opts);
      case 'idle':
        return this.idle(target);
      default:
        return waitMs(100);
    }
  }

  private async attackRun(s: CharacterSprite): Promise<void> {
    const baseSpeed = s.timings.run?.speed ?? 1;
    s.playAnimation('run', { speed: baseSpeed });
    const lunge = ATTACK_LUNGE_DISTANCE * s.facing;
    // Sync leg cycle to body velocity. sine.out is gentler than power2.out -
    // velocity factor goes from ~1.57 to 0 (vs 2 to 0), so the deceleration
    // reads smoother and legs don't crawl as visibly at the end.
    // Derivative of sine.out (f = sin(t * pi/2)) is (pi/2) * cos(t * pi/2).
    const tween = gsap.to(s, {
      x: s.baseX + lunge,
      duration: 0.4,
      ease: 'sine.out',
      onUpdate: () => {
        const t = tween.progress();
        const v = (Math.PI / 2) * Math.cos((t * Math.PI) / 2);
        s.setAnimationSpeed(baseSpeed * Math.max(v, RUN_MIN_SPEED_FACTOR));
      },
    });
    await tween;
  }

  private async attackSwing(s: CharacterSprite, opts?: PlayOptions): Promise<void> {
    // Default attack is punch OR kick (chosen by the engine, passed via opts).
    // One-shot up to the impact frame, then resolve. Recovery frames keep
    // playing in parallel with the renderer's hit + post-damage pause;
    // attackReturn awaits the rest before dashing back.
    // Note: impact SFX is fired by the renderer based on defender reaction
    // (no thud sound on dodge - nothing to hit).
    const anim = opts?.attackAnim ?? 'punch';
    const t = s.timings[anim];
    const scaleMultiplier = t && 'scale' in t ? t.scale : undefined;
    s.playAnimation(anim, { loop: false, speed: t?.speed, scaleMultiplier });
    const impactFrame = t && 'impactFrame' in t ? (t.impactFrame ?? 0) : 0;
    await s.awaitFrame(impactFrame);
  }

  private async attackReturn(s: CharacterSprite): Promise<void> {
    // Wait for the punch's recovery frames to finish before retreating.
    // (No-op if punch already finished during the post-damage pause.)
    await s.awaitAnimationComplete();
    // Mirror the sprite horizontally so the running anim reads as moving in
    // the actual direction of travel (away from the opponent).
    s.setMirrored(true);
    s.playAnimation('run', { speed: s.timings.run?.speed });
    await gsap.to(s, { x: s.baseX, duration: 0.3, ease: 'power2.in' });
    s.setMirrored(false);
    s.playAnimation('idle');
  }

  private async counterJab(s: CharacterSprite, opts?: PlayOptions): Promise<void> {
    // Counter swings punch or kick (engine-rolled, passed via opts) - the
    // original attacker is already in melee range, so no lunge tween needed.
    // Await up to the impact frame so the renderer's following `damage` event
    // (hit on attacker) lands during the swing rather than after recovery.
    const anim = opts?.attackAnim ?? 'punch';
    if (s.hasAnimation(anim)) {
      const t = s.timings[anim];
      const scaleMultiplier = t && 'scale' in t ? t.scale : undefined;
      s.playAnimation(anim, { loop: false, speed: t?.speed, scaleMultiplier });
      // Recovery frames continue in background after we return at impact;
      // schedule idle snap so they don't freeze on the last frame waiting for
      // round_end. (attackSwing is symmetric but its cleanup lives in
      // attackReturn; counter has no equivalent retreat phase.)
      s.scheduleIdleAfterCurrentAnim();
      const impactFrame = t && 'impactFrame' in t ? (t.impactFrame ?? 0) : 0;
      await s.awaitFrame(impactFrame);
      // Impact SFX fired by the renderer (counter always lands - no need to
      // branch on reaction here, but consistency: visuals own visuals, sfx
      // owned by renderer/skill bundle).
      return;
    }
    // Fallback for placeholder/Graphics mode without the anim sheet.
    const jab = COUNTER_JAB_DISTANCE * s.facing;
    await gsap.to(s, { x: s.baseX + jab, duration: 0.12, ease: 'power2.out' });
    await gsap.to(s, { x: s.baseX, duration: 0.15, ease: 'power2.in' });
  }

  private block(s: CharacterSprite, opts?: PlayOptions): Promise<void> {
    // Hold pose at the character's `holdFrame` for `holdDurationMs` so the block
    // reads as a sustained stance instead of a flick. Both come from the
    // character bundle's timings.
    // loop:false - block must NOT recycle. Otherwise blockExit's reverse-from-
    // current-frame would run on whatever loop iteration we happen to be in,
    // producing a partial / nonsensical exit instead of unwinding the full enter.
    // speedOverride lets the renderer auto-sync block enter with the attacker's
    // punch impact frame (so guard is up exactly when the fist lands).
    const t = s.timings.block;
    s.playAnimation('block', {
      loop: false,
      speed: opts?.speedOverride ?? t?.speed,
      ...(t?.holdFrame !== undefined && t.holdDurationMs !== undefined
        ? { pauseAtFrame: { frame: t.holdFrame, durationMs: t.holdDurationMs } }
        : {}),
    });
    s.playSfx('block');
    return Promise.resolve();
  }

  private blockExit(s: CharacterSprite): Promise<void> {
    // Reverse the block sheet from current frame (typically the hold pose)
    // back to frame 0, no hold pause. Lets the defender visibly "lower their
    // guard" as the attacker retreats. Uses the character's block speed.
    return s.playReverseToStart({ speed: s.timings.block?.speed });
  }

  private async dodge(s: CharacterSprite): Promise<void> {
    // Sprite anim (lean/dip in place) plays in parallel with the sidestep tween
    // (actual horizontal displacement). Both contribute - anim conveys the
    // motion, tween adds physical "got out of the way" feel.
    const hasAnim = s.hasAnimation('dodge');
    if (hasAnim) {
      s.playAnimation('dodge', { loop: false, speed: s.timings.dodge?.speed });
    }
    const aside = -40 * s.facing;
    const tweenPromise = new Promise<void>((resolve) => {
      const tl = gsap.timeline({ onComplete: resolve });
      tl.to(s, { x: s.baseX + aside, duration: 0.1, ease: 'power2.out' })
        .to(s, { x: s.baseX, duration: 0.15, ease: 'power2.in' });
    });
    if (hasAnim) {
      await Promise.all([tweenPromise, s.awaitAnimationComplete()]);
      // Snap back so we don't freeze on the last dodge frame.
      s.playAnimation('idle', { speed: s.timings.idle?.speed });
    } else {
      await tweenPromise;
    }
  }

  private async hit(s: CharacterSprite, opts?: PlayOptions): Promise<void> {
    // Damage sounds belong to the SOURCE (attacker's punch / skill impact),
    // not the recipient's plain pain. The attacker fires its own punch sfx;
    // skills play their own impact clips. EXCEPTION: the pain-air knock-up has
    // its own reaction sfx (played by the defender below) - a launch is
    // dramatic enough to warrant a dedicated recipient sound.
    // Prefer the pain animation when available; fall back to the placeholder
    // tint+shake for Graphics-mode characters or if pain frames aren't loaded.
    // Launching hit -> the pain-air reaction. The knock-up motion is baked
    // into that sheet, so there's no programmatic vertical move here. Only
    // when the character has a painAir sheet; otherwise fall back to pain.
    const air = !!opts?.launches && s.hasAnimation('painAir');
    const animName = air ? 'painAir' : 'pain';
    if (s.hasAnimation(animName)) {
      const t = s.timings[animName];
      s.playAnimation(animName, { loop: false, speed: t?.speed, scaleMultiplier: t?.scale });
      // Pain-air launch sound, fired with the reaction's first frame.
      if (air) s.playSfx('painAir');
      // Wait for the visible-impact frame (if defined) before firing damage
      // visuals. Without an impactFrame the callback fires at the start of
      // the anim - matches the pre-impactFrame behavior.
      if (t?.impactFrame !== undefined) await s.awaitFrame(t.impactFrame);
      // Red flash + scene-side damage visuals (text/HP/particles via opts.onImpact)
      // land together on impact. Wall-time flash so it always feels snappy.
      s.setBodyColor(HIT_COLOR);
      setTimeout(() => s.restoreBodyColor(), HIT_FLASH_MS);
      if (opts?.onImpact) await opts.onImpact();
      await s.awaitAnimationComplete();
      // dead - stay down on the last pain frame, no idle snap
      if (opts?.died) return;
      // Snap back to idle so the character doesn't freeze on the last pain frame.
      s.playAnimation('idle');
      return;
    }
    // Hit-flash fallback (Graphics mode / no pain frames). Fire damage visuals
    // immediately - there's no anim with a meaningful impact frame here.
    if (opts?.onImpact) await opts.onImpact();
    return this.hitFlash(s);
  }

  private hitFlash(s: CharacterSprite): Promise<void> {
    return new Promise((resolve) => {
      const startX = s.x;
      s.setBodyColor(HIT_COLOR);
      const tl = gsap.timeline({
        onComplete: () => {
          // Restore color but keep stance (e.g. block scale) intact.
          s.restoreBodyColor();
          resolve();
        },
      });
      tl.to(s, { x: startX + 6, duration: 0.04 })
        .to(s, { x: startX - 6, duration: 0.06 })
        .to(s, { x: startX, duration: 0.04 });
    });
  }

  private idle(s: CharacterSprite): Promise<void> {
    return new Promise((resolve) => {
      this.stop(s);
      s.position.set(s.baseX, s.baseY);
      s.resetBody();
      s.playAnimation('idle', { speed: s.timings.idle?.speed });
      resolve();
    });
  }
}

function waitMs(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
